import { actionDecision } from "../page/viewerActions";
import { StopIcon } from "@heroicons/react/24/outline";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { cancelTurn } from "../../../api/threads/threadsApi";
import ErrorNotice from "../../../errors/ErrorNotice";
import { isProblemWithCode, toUiError } from "../../../errors/uiError";
import { useSubmission } from "../../../shared/state/useSubmission";
import AppButton from "../../../ui/buttons/AppButton";
import { refusalText } from "../../../ui/feedback/refusalWords";
import ProgressBar from "../../../ui/feedback/ProgressBar";
import SpinnerIcon from "../../../ui/feedback/SpinnerIcon";
import { useThreadContext } from "../page/threadPageContext";
import { formatElapsed, turnActivityKey } from "../status/turnActivity";
import OpenOnPc from "../../connectors/pcPage/OpenOnPc";
import ToolApprovalNotice from "../entries/ToolApprovalNotice";
import { useApprovalTarget } from "../entries/useApprovalPage";

/**
 * The open turn: what state it is in, how long since it was asked for, a tool's share of its
 * work done (F67), and cancelling it (T-4 turn machine). A turn the host's PC has no free slot
 * for says so, with how long it has waited, never that the agent works (X-2).
 */
export default function TurnControls() {
  const { t } = useTranslation();
  const { threadId, openTurn, stepName, runApprovals, viewerIsHost, overview } = useThreadContext();
  const { state, submit } = useSubmission();
  // While it waits for a slot, the host sees their PC by name, how many agents run there at once
  // and where that is changed (CN-14); anyone else, "the host's PC".
  const waitsForSlot = openTurn?.displayState === "slot";
  const target = useApprovalTarget(viewerIsHost && waitsForSlot ? overview.attachedConnector : null, waitsForSlot);
  const hostPc = target?.pc ?? t("threads.toolApproval.hostPc");
  const agentLimit = waitsForSlot && openTurn.executor === "agent" && typeof target?.agentsAtOnce === "number" ? target.agentsAtOnce : null;
  if (openTurn === null) {
    return null;
  }
  const cancelling = openTurn.state === "cancelling" || state.status === "submitting";
  const cancelDecision = actionDecision(overview.viewer, "cancel_turn", openTurn.turnId);

  const cancel = () => {
    submit(async () => {
      try {
        await cancelTurn(threadId, openTurn.turnId);
      } catch (caught) {
        // Finished meanwhile: the result is in the log.
        if (!isProblemWithCode(toUiError(caught), "illegal_state_transition")) throw caught;
      }
    });
  };

  return (
    <div className="space-y-2">
      <div className="max-h-[45vh] space-y-2 overflow-y-auto">
        {[...runApprovals].map(([turnId, approval]) => (
          <ToolApprovalNotice
            key={`${turnId}:${approval.answerable?.pending.request_id ?? ""}`}
            approval={approval}
            waiting={true}
            hostConnectorId={viewerIsHost ? overview.attachedConnector : null}
          />
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-2 text-xs" aria-live="polite">
        {/* Turning only while C says it queues or runs: never while it waits, nor once its record ended (Sol code review c2, I1). */}
        {openTurn.displayState === "running" || openTurn.displayState === "queued" ? <SpinnerIcon className="h-3.5 w-3.5" /> : null}
        <span className="app-text min-w-0 flex-1">
          {t(turnActivityKey(openTurn), { pc: hostPc })}
          {openTurn.stepKey === null ? null : `: ${stepName(openTurn.stepKey)}`}
        </span>
        {openTurn.modelBytes === null ? null : (
          <span className="app-text-muted tabular-nums">{t("threads.turnActivity.modelReceived", { megabytes: Math.round(openTurn.modelBytes / 1_000_000) })}</span>
        )}
        {openTurn.work === null ? null : (
          <span className="flex items-center gap-2">
            <ProgressBar value={(openTurn.work.done / openTurn.work.total) * 100} className="w-32" />
            <span className="app-text-muted tabular-nums">{t("threads.turnActivity.workDone", { done: openTurn.work.done, total: openTurn.work.total })}</span>
          </span>
        )}
        <Elapsed since={openTurn.waitingForSlotSince ?? openTurn.requestedAt} />
        <AppButton
          variant="danger"
          size="xs"
          onClick={cancel}
          disabled={cancelling}
          unavailable={cancelDecision.refusal === null ? undefined : refusalText(t, cancelDecision.refusal)}
        >
          <StopIcon className="h-3.5 w-3.5" aria-hidden="true" />
          {cancelling ? t("threads.composer.cancelling") : t("threads.composer.cancelTurn")}
        </AppButton>
        {state.status === "failed" ? <ErrorNotice error={state.error} compact={true} /> : null}
      </div>
      {agentLimit === null || target === null ? null : (
        <div className="space-y-1 text-xs">
          <p className="app-text-muted">{t("threads.turnActivity.agentsAtOnce", { count: agentLimit })}</p>
          {target.pageServed ? (
            <OpenOnPc
              connectorId={target.connectorId}
              pc={target.pc}
              section="agents"
              label={t("threads.turnActivity.changeAgentsAtOnce", { pc: target.pc })}
              command={{ args: "tools page", aboutServer: true }}
              variant="secondary"
            />
          ) : null}
        </div>
      )}
    </div>
  );
}

/** The time since the turn was asked for, counted on every second. */
function Elapsed({ since }: { since: string }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => { setNow(Date.now()); }, 1000);
    return () => { window.clearInterval(timer); };
  }, []);
  return <span className="app-text-muted tabular-nums">{formatElapsed(now - Date.parse(since))}</span>;
}
